<template>
  <div>
    <div ref="dropdownRef" class="relative w-full">
      <button
        type="button"
        class="flex items-center gap-2 text-ellipsis w-full px-4 py-3 text-left border bg-white border-secondary-400 rounded text-md shadow-sm focus:outline-none focus:ring-1 focus:ring-secondary-900 disabled:cursor-not-allowed disabled:bg-gray-100"
        aria-haspopup="listbox"
        :disabled="props.disabled"
        @click="toggleDropdown"
      >
        <span class="overflow-hidden whitespace-nowrap text-ellipsis">
          {{ translate(selectedLabel || placeholder, props.pageLang) }}
        </span>
        <button
          v-if="selectedLabel"
          class="ms-auto focus:ring-secondary-900"
          @click.stop.prevent="selectOption({ label: '', value: null, id: 0 })"
        >
          <CircleX
            v-if="props.allowRemoveSelected"
            :size="16"
            class="text-red-400"
          />
        </button>
        <ChevronDown
          :size="16"
          :class="{ 'rotate-180': isOpen, 'ms-auto': !selectedLabel }"
          class="text-font-800 transition-all ease-in-out duration-300"
        />
      </button>

      <!-- Animated Dropdown -->
      <transition
        name="dropdown"
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <ul
          v-if="isOpen"
          class="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto focus:ring-secondary-900"
          role="listbox"
        >
          <li
            v-for="option in options"
            :key="option.value"
            class="px-4 py-3 cursor-pointer hover:bg-secondary-200 transition-colors duration-150"
            role="option"
            :aria-selected="option.value === modelValue"
            @click="selectOption(option)"
          >
            {{ translate(option.label, props.pageLang) }}
          </li>
        </ul>
      </transition>
    </div>
    <VValidator :dataValidationId="props.dataValidationId" />
  </div>
</template>

<script setup lang="ts">
interface Option {
  label: string
  value: string | number | any
  selected?: boolean
  id: number
}

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ChevronDown, CircleX } from 'lucide-vue-next'
import { translate } from 'src/utils/translation'

const props = defineProps<{
  modelValue: string | number | null | any
  options: Option[]
  placeholder?: any
  allowRemoveSelected?: boolean
  pageLang?: string
  pageDir?: string
  dataValidationId?: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null | any): void
  (e: 'change', value: string | number | null | any): void
  (e: 'open'): void
  (e: 'close'): void
}>()

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const selectedLabel = computed(
  () =>
    props.options.find(opt => opt.value === props.modelValue?.value)?.label ??
    '',
)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    emit('open')
  } else {
    emit('close')
  }
}

const selectOption = (option: Option) => {
  emit('update:modelValue', option)
  emit('change', option)
  isOpen.value = false
  emit('close')
}

const closeOnClickOutside = (e: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    if (isOpen.value) {
      isOpen.value = false
      emit('close')
    }
  }
}

onMounted(() => {
  document.addEventListener('click', closeOnClickOutside)

  // set default selected if exists
  if (props.options.some(opt => opt.selected === true)) {
    const selectedOption = props.options.find(opt => opt.selected === true)
    if (selectedOption) {
      emit('update:modelValue', selectedOption)
      emit('change', selectedOption)
    }
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeOnClickOutside)
})
</script>
